import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ReportingService } from "../../services/reporting.service";
import { ReportMetadata, ReportFilter, ReportData, ReportGraph, ReportFilterConditional, ReportSelectOption } from "../../models/report.model";

@Component({
  selector: "app-report-detail",
  templateUrl: "./report-detail.component.html",
  styleUrls: ["./report-detail.component.scss"]
})
export class ReportDetailComponent implements OnInit {
  reportType: string = "";
  reportTitle: string = "";
  reportMetadata: ReportMetadata | null = null;
  reportFilters: ReportFilter[] = [];
  filterForm: FormGroup;
  isLoading: boolean = false;
  responseReceived: boolean = false;
  reportData: ReportData;

  graphData: any[] = [];
  searchTexts: Record<string, string> = {};

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private reportingService: ReportingService
  ) {
    this.filterForm = this.fb.group({});
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.reportType = params["reportType"];
      this.loadReportMetadata();
    });
  }

  loadReportMetadata(): void {
    this.reportingService.getReportMetadata(this.reportType).subscribe({
      next: (metadata) => {
        this.reportMetadata = metadata;
        this.reportTitle = metadata.name;
        this.reportFilters = metadata.filters || [];
        this.initializeGraphs();
        this.buildFilterForm();
      },
      error: (error) => {
        console.error("Error loading report metadata:", error);
        this.reportTitle = "Report";
      }
    });
  }

  initializeGraphs() {
    this.reportMetadata?.graphs?.forEach((graph, i) => {
      this.reportMetadata.graphs[i].xAxisFormatFunction = this.formatXAxis(graph)
      this.reportMetadata.graphs[i].yAxisFormatFunction = this.formatYAxis(graph)
    })
  }

  buildFilterForm(): void {
    const formControls: { [key: string]: any } = {};

    this.reportFilters.forEach(filter => {
      if (filter.name) {
        const defaultValue = filter.type === 'date' ? null : '';
        formControls[filter.name] = [defaultValue];
        if (!filter.optional) {
          formControls[filter.name].push(Validators.required);
        }
      }
    });

    if (this.reportMetadata?.cacheable) {
      formControls["useCache"] = [true];
    }

    this.filterForm = this.fb.group(formControls);
  }

  getFilterLabel(filter: ReportFilter): string {
    if (!filter.name) return '';

    // Convert camelCase or snake_case to Title Case
    return filter.name
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }

  generateReport(): void {
    this.isLoading = true;
    this.responseReceived = false;

    let values = {}
    Object.entries(this.filterForm.value).forEach(([key, value]) => {
      if (this.reportFilters.find(f => f.name === key)?.type === 'date' && value) {
        values[key] = (value as Date).toISOString();
      }
      else if (value) {
        values[key] = value;
      }
    });

    this.reportingService.generateReport(this.reportType, values).subscribe({
      next: (data) => {
        this.reportData = data;
        this.graphData = this.formatGraphData(data);
        this.isLoading = false;
        this.responseReceived = true;
      },
      error: (error) => {
        console.error("Error generating report:", error);
        this.isLoading = false;
      }
    });
  }

  convertToCSV(): string {
    if (!this.reportData) {
      return '';
    }

    const headers = this.reportData.headers.map(h => this.escapeCSVField(h.label));
    const headerRow = headers.join(',');

    const rows = this.reportData.rows.map(row => {
      return this.reportData.headers.map(header => {
        const value = row[header.field];
        return this.escapeCSVField(value);
      }).join(',');
    });

    return [headerRow, ...rows].join('\n');
  }

  private escapeCSVField(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const stringValue = String(value);

    // If the field contains comma, newline, or quotes, wrap it in quotes and escape internal quotes
    if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  }

  exportCsv(): void {
    const csv = this.convertToCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = window.URL.createObjectURL(blob);

    link.href = url;
    link.download = `${this.reportType}-report.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  goBack(): void {
    this.router.navigate(["/reports"]);
  }

  formatXAxis(graph: ReportGraph): (value: string) => string {
    if (graph.xAxisFormat === 'date') return this.formatDateTick;
    if (graph.xAxisFormat === 'percentage') return this.formatPercentageTick;
    return (value: string) => value;
  }

  formatYAxis(graph: ReportGraph): (value: string) => string {
    if (graph.yAxisFormat === 'date') return this.formatDateTick;
    if (graph.yAxisFormat === 'percentage') return this.formatPercentageTick;
    return (value: string) => value;
  }

  formatDateTick(value: string): string {
    if (!value) return '';
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  formatPercentageTick(value: string) {
    if (!value) return '';
    return String(Number(value) * 100) + '%'
  }

  formatGraphData(data: ReportData): any {
    let graphData: any[] = [];

    if (!data || !data.headers || !data.rows) {
      return graphData;
    }

    for (const graphMetadata of this.reportMetadata?.graphs || []) {
      if (graphMetadata.type === 'line') {
        graphData.push(this.formatLineGraphData(graphMetadata, data));
      }

      if (graphMetadata.type === 'bar') {
        graphData.push(this.formatBarGraphData(graphMetadata, data));
      }
    }
    return graphData;
  }

  formatLineGraphData(graphMetadata: ReportGraph, data: ReportData): any {
    const xLineField = graphMetadata.xAxisField;

    const xLineHeader = data.headers.find(h => h.field === xLineField);
    if (!xLineHeader) {
      return [];
    }

    const dataHeaders = data.headers.filter(h => graphMetadata.includedFields.includes(h.field));

    return dataHeaders.map(header => ({
      name: header.label,
      series: data.rows.map(row => ({
        name: row[xLineHeader.field].value,
        value: Number(row[header.field].value) || 0
      }))
    }));
  }

  formatBarGraphData(graphMetadata: ReportGraph, data: ReportData): any {
    const xBarField = graphMetadata.xAxisField;

    const xBarHeader = data.headers.find(h => h.field === xBarField);
    if (!xBarHeader) {
      return [];
    }

    const dataRows = data.rows
    const dataHeaders = data.headers.filter(h => graphMetadata.includedFields.includes(h.field));

    return dataRows.map(row => ({
      name: row[xBarHeader.field].value,
      series: dataHeaders.map(header => ({
        name: header.label,
        value: Number(row[header.field].value) || 0
      }))
    }));
  }


  evaluateConditional(conditional: ReportFilterConditional[]): boolean {
    return conditional.every(c => this.filterForm.get(c.field)?.value === c.value);
  }

  getFilteredOptions(filter: ReportFilter): ReportSelectOption[] {
    const search = (this.searchTexts[filter.name!] || '').toLowerCase();
    if (!search) return filter.options || [];
    return (filter.options || []).filter(opt => opt.label.toLowerCase().includes(search));
  }

  clearSearch(filterName: string): void {
    this.searchTexts[filterName] = '';
  }
}